import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { authFetch } from '../lib/authFetch';
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
  Button, Input, Badge, Select, Modal, ModalFooter, StatCard, Textarea,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
  Tabs, TabsList, TabsTrigger, TabsContent
} from '../components/ui';
import { formatDate, formatRelativeTime, formatCurrency } from '../lib/utils';
import {
  Users, DollarSign, Shield, Search, Send,
  UserCheck, UserX, Eye, Trash2, RefreshCw, MessageSquare,
  Filter, X, Globe, Layers, Mail, Clock, Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Display labels for platforms / features ────────────────────────────────
const PLATFORM_LABEL = {
  meta:           'Meta Ads',
  google:         'Google Ads',
  tiktok:         'TikTok Ads',
  shopify:        'Shopify',
  adsense:        'AdSense',
  admanager:      'Ad Manager',
  hapiguard:      'HapiGuard',
  custom_domain:  'Custom Domain',
};
const FEATURE_LABEL = {
  landing_pages:   'Landing pages',
  ab_tests:        'A/B tests',
  email_campaigns: 'Email campaigns',
  email_contacts:  'Email contacts',
};

export const Admin = () => {
  const { user, profile } = useAuth();
  const isSuperAdmin = profile?.role === 'superadmin';
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ totalUsers: 0, activeUsers: 0, totalRevenue: 0, openTickets: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Demographics + filters (Users tab only)
  const [aggregates, setAggregates] = useState(null);
  const [filterTier,     setFilterTier]     = useState('all');
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterFeature,  setFilterFeature]  = useState('all');
  
  // Ticket detail state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // ── Enriched users + aggregates (admin-only endpoint) ──
      // One round trip; counts platform connections + feature usage per user.
      let usersData = [];
      try {
        const res  = await authFetch('/api/admin-users');
        const data = await res.json();
        if (data.success) {
          usersData = data.users || [];
          setAggregates(data.aggregates || null);
        } else {
          throw new Error(data.error || 'admin-users failed');
        }
      } catch (e) {
        // Graceful fallback: pull raw profiles so the page still renders
        // even if the new endpoint hasn't deployed yet.
        console.warn('[admin] enriched users load failed, falling back:', e.message);
        const { data, error } = await supabase
          .from('profiles').select('*').order('created_at', { ascending: false });
        if (error) throw error;
        usersData = (data || []).map(p => ({ ...p, email: null, platforms: [], features: {} }));
        setAggregates(null);
      }
      setUsers(usersData);

      // Load tickets (without join - we'll map user info manually)
      const { data: ticketsData, error: ticketsError } = await supabase
        .from('support_tickets')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (ticketsError) {
        console.log('Tickets table might not exist yet:', ticketsError.message);
      }

      // Map user info to tickets
      const ticketsWithUsers = (ticketsData || []).map(ticket => ({
        ...ticket,
        user: usersData?.find(u => u.id === ticket.user_id) || null
      }));
      setTickets(ticketsWithUsers);

      // Calculate stats
      const totalUsers = usersData?.length || 0;
      const activeUsers = usersData?.filter(u => u.status !== 'suspended' && u.status !== 'banned').length || totalUsers;
      const openTickets = ticketsWithUsers?.filter(t => t.status === 'open' || t.status === 'in_progress').length || 0;

      // Calculate total revenue from AdSense and Ad Manager
      let totalRevenue = 0;
      
      try {
        // Get AdSense revenue (net_revenue)
        const { data: adsenseData } = await supabase
          .from('adsense_daily')
          .select('net_revenue');
        
        if (adsenseData) {
          totalRevenue += adsenseData.reduce((sum, row) => sum + (parseFloat(row.net_revenue) || 0), 0);
        }

        // Get Ad Manager revenue (net_revenue)
        const { data: admanagerData } = await supabase
          .from('admanager_daily')
          .select('net_revenue');
        
        if (admanagerData) {
          totalRevenue += admanagerData.reduce((sum, row) => sum + (parseFloat(row.net_revenue) || 0), 0);
        }
      } catch (err) {
        console.log('Revenue tables may not exist yet:', err.message);
      }

      setStats({
        totalUsers,
        activeUsers,
        totalRevenue,
        openTickets,
      });
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadTicketMessages = async (ticketId) => {
    try {
      const { data, error } = await supabase
        .from('ticket_replies')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setTicketMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      setTicketMessages([]);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (error) throw error;

      toast.success('User updated');
      loadData();
      setShowUserModal(false);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', ticketId);

      if (error) throw error;

      toast.success('Ticket status updated');
      
      // Update local state
      setTickets(tickets.map(t => t.id === ticketId ? { ...t, status } : t));
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status });
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Failed to update ticket');
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;

    setSendingReply(true);
    try {
      // Insert admin reply
      const { error: messageError } = await supabase
        .from('ticket_replies')
        .insert({
          ticket_id: selectedTicket.id,
          user_id: user.id,
          message: replyMessage.trim(),
          is_admin_reply: true
        });

      if (messageError) throw messageError;

      // Update ticket status to in_progress if it was open
      if (selectedTicket.status === 'open') {
        await supabase
          .from('support_tickets')
          .update({ status: 'in_progress', updated_at: new Date().toISOString() })
          .eq('id', selectedTicket.id);
        
        setSelectedTicket({ ...selectedTicket, status: 'in_progress' });
        setTickets(tickets.map(t => t.id === selectedTicket.id ? { ...t, status: 'in_progress' } : t));
      }

      toast.success('Reply sent');
      setReplyMessage('');
      loadTicketMessages(selectedTicket.id);
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const openTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
    setReplyMessage('');
    await loadTicketMessages(ticket.id);
  };

  // Filter users by every active filter chip + the search query.
  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return users.filter(u => {
      if (q) {
        const haystack = `${u.username || ''} ${u.email || ''} ${u.id || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filterTier   !== 'all' && (u.tier || u.subscription_plan || 'trial') !== filterTier) return false;
      if (filterStatus !== 'all' && (u.status || 'active') !== filterStatus) return false;
      if (filterPlatform !== 'all') {
        if (!Array.isArray(u.platforms) || !u.platforms.includes(filterPlatform)) return false;
      }
      if (filterFeature !== 'all') {
        const v = u.features?.[filterFeature];
        if (!v || v <= 0) return false;
      }
      return true;
    });
  }, [users, searchQuery, filterTier, filterStatus, filterPlatform, filterFeature]);

  const activeFilters = [
    filterTier     !== 'all' && { key: 'tier',     label: `Tier: ${filterTier}`,           clear: () => setFilterTier('all') },
    filterStatus   !== 'all' && { key: 'status',   label: `Status: ${filterStatus}`,       clear: () => setFilterStatus('all') },
    filterPlatform !== 'all' && { key: 'platform', label: `Platform: ${PLATFORM_LABEL[filterPlatform] || filterPlatform}`, clear: () => setFilterPlatform('all') },
    filterFeature  !== 'all' && { key: 'feature',  label: `Uses: ${FEATURE_LABEL[filterFeature] || filterFeature}`,        clear: () => setFilterFeature('all') },
  ].filter(Boolean);
  const clearAllFilters = () => {
    setFilterTier('all'); setFilterStatus('all');
    setFilterPlatform('all'); setFilterFeature('all');
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'warning',
      in_progress: 'default',
      resolved: 'success',
      closed: 'secondary'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status?.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Admin Panel</h1>
          <p className="text-muted-foreground">Manage users and support tickets</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Total Users"
          value={stats.totalUsers.toString()}
          icon={<Users className="h-6 w-6" />}
        />
        <StatCard
          title="Active Users"
          value={stats.activeUsers.toString()}
          icon={<UserCheck className="h-6 w-6" />}
        />
        <StatCard
          title="Open Tickets"
          value={stats.openTickets.toString()}
          icon={<MessageSquare className="h-6 w-6" />}
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats.totalRevenue)}
          icon={<DollarSign className="h-6 w-6" />}
          description="AdSense + Ad Manager"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="tickets">
            Support Tickets
            {stats.openTickets > 0 && (
              <Badge variant="destructive" className="ml-2">{stats.openTickets}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">

          {/* ── Demographics row ─────────────────────────────────────────── */}
          <DemographicsRow aggregates={aggregates} loading={loading} />

          <Card className="mt-4">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>{filteredUsers.length} users found</CardDescription>
                </div>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search email / username / id…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* ── Filter bar ───────────────────────────────────────────── */}
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select value={filterTier} onChange={e => setFilterTier(e.target.value)}
                  className="text-sm border border-border rounded-md px-2 py-1.5 bg-card text-foreground">
                  <option value="all">All tiers</option>
                  {Object.keys(aggregates?.tier || {}).map(k => <option key={k} value={k}>{k}</option>)}
                </select>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  className="text-sm border border-border rounded-md px-2 py-1.5 bg-card text-foreground">
                  <option value="all">All statuses</option>
                  {Object.keys(aggregates?.status || {}).map(k => <option key={k} value={k}>{k}</option>)}
                </select>
                <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)}
                  className="text-sm border border-border rounded-md px-2 py-1.5 bg-card text-foreground">
                  <option value="all">Any platform</option>
                  {Object.keys(PLATFORM_LABEL).map(k => (
                    <option key={k} value={k}>{PLATFORM_LABEL[k]}</option>
                  ))}
                </select>
                <select value={filterFeature} onChange={e => setFilterFeature(e.target.value)}
                  className="text-sm border border-border rounded-md px-2 py-1.5 bg-card text-foreground">
                  <option value="all">Any feature</option>
                  {Object.keys(FEATURE_LABEL).map(k => (
                    <option key={k} value={k}>{FEATURE_LABEL[k]}</option>
                  ))}
                </select>

                {activeFilters.length > 0 && (
                  <>
                    <div className="w-px h-5 bg-border mx-1" />
                    {activeFilters.map(f => (
                      <Badge key={f.key} variant="secondary" className="gap-1 cursor-pointer" onClick={f.clear}>
                        {f.label} <X className="h-3 w-3" />
                      </Badge>
                    ))}
                    <button onClick={clearAllFilters} className="text-xs text-muted-foreground hover:text-foreground underline">
                      Clear all
                    </button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <TableSkeleton />
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead className="hidden md:table-cell">Email</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead className="hidden sm:table-cell">Status</TableHead>
                        <TableHead className="hidden lg:table-cell">Platforms</TableHead>
                        <TableHead className="hidden lg:table-cell">Last seen</TableHead>
                        <TableHead className="w-[60px]">{/* actions */}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableEmpty colSpan={7} message="No users match the current filters" />
                      ) : (
                        filteredUsers.slice(0, 100).map((u) => {
                          const tierKey = u.tier || u.subscription_plan || 'trial';
                          return (
                            <TableRow
                              key={u.id}
                              className="cursor-pointer hover:bg-muted/40"
                              onClick={() => { setSelectedUser(u); setShowUserModal(true); }}
                            >
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-medium flex-shrink-0">
                                    {(u.username?.[0] || u.email?.[0] || 'U').toUpperCase()}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium truncate">{u.username || u.email?.split('@')[0] || 'No username'}</p>
                                    <p className="text-xs text-muted-foreground">
                                      <Badge variant={u.role === 'admin' || u.role === 'superadmin' ? 'default' : 'secondary'} className="mr-1">
                                        {u.role || 'user'}
                                      </Badge>
                                    </p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-sm text-muted-foreground truncate max-w-[220px]">
                                {u.email || '—'}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">{tierKey}</Badge>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell">
                                <Badge variant={u.status === 'active' ? 'success' : 'destructive'}>
                                  {u.status || 'active'}
                                </Badge>
                              </TableCell>
                              <TableCell className="hidden lg:table-cell">
                                {Array.isArray(u.platforms) && u.platforms.length > 0
                                  ? (
                                    <div className="flex flex-wrap gap-1 max-w-[280px]">
                                      {u.platforms.slice(0, 4).map(p => (
                                        <Badge key={p} variant="secondary" className="text-[10px]">
                                          {PLATFORM_LABEL[p] || p}
                                        </Badge>
                                      ))}
                                      {u.platforms.length > 4 && (
                                        <Badge variant="outline" className="text-[10px]">+{u.platforms.length - 4}</Badge>
                                      )}
                                    </div>
                                  )
                                  : <span className="text-xs text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                                {u.last_login_at ? formatRelativeTime(u.last_login_at) : <span className="text-muted-foreground/60">never</span>}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedUser(u); setShowUserModal(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets">
          <Card>
            <CardHeader>
              <CardTitle>Support Tickets</CardTitle>
              <CardDescription>
                {tickets.filter(t => t.status === 'open').length} open, {tickets.filter(t => t.status === 'in_progress').length} in progress
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No support tickets</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      onClick={() => openTicketDetail(ticket)}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 cursor-pointer transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{ticket.subject}</p>
                          {ticket.priority === 'urgent' && (
                            <Badge variant="destructive">Urgent</Badge>
                          )}
                          {ticket.priority === 'high' && (
                            <Badge variant="warning">High</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {ticket.user?.username || 'Unknown user'} • {formatRelativeTime(ticket.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(ticket.status)}
                        <Button variant="ghost" size="icon">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Detail Modal */}
      <Modal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title="User Details"
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-5">

            {/* ── Header card: avatar + name + email + ids ─────────────── */}
            <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-border">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary text-lg font-semibold flex-shrink-0">
                {(selectedUser.username?.[0] || selectedUser.email?.[0] || 'U').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-foreground truncate">
                  {selectedUser.username || selectedUser.email?.split('@')[0] || 'No username'}
                </p>
                <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                  {selectedUser.email || <span className="text-muted-foreground/60">No email on auth account</span>}
                </p>
                <p className="text-xs text-muted-foreground font-mono mt-1 truncate" title={selectedUser.id}>
                  {selectedUser.id}
                </p>
              </div>
            </div>

            {/* ── Editable: role / status / plan ───────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Role</p>
                <Select
                  value={selectedUser.role || 'user'}
                  onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                  disabled={!isSuperAdmin}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </Select>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Status</p>
                <Select
                  value={selectedUser.status || 'active'}
                  onChange={(e) => setSelectedUser({ ...selectedUser, status: e.target.value })}
                  disabled={!isSuperAdmin}
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="banned">Banned</option>
                </Select>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Plan / Tier</p>
                <Select
                  value={selectedUser.tier || selectedUser.subscription_plan || 'trial'}
                  onChange={(e) => setSelectedUser({ ...selectedUser, tier: e.target.value, subscription_plan: e.target.value })}
                  disabled={!isSuperAdmin}
                >
                  <option value="trial">Trial</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                </Select>
              </div>
            </div>

            {/* ── Activity + demographics ──────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <DetailRow label="Joined"          icon={Clock}>
                {selectedUser.created_at ? formatDate(selectedUser.created_at) : '—'}
              </DetailRow>
              <DetailRow label="Last seen"       icon={Activity}>
                {selectedUser.last_login_at ? `${formatRelativeTime(selectedUser.last_login_at)} (${formatDate(selectedUser.last_login_at)})` : <span className="text-muted-foreground">Never</span>}
              </DetailRow>
              <DetailRow label="Last login IP"   icon={Globe}>
                <span className="font-mono text-xs">{selectedUser.last_login_ip || '—'}</span>
              </DetailRow>
              <DetailRow label="Registration IP" icon={Globe}>
                <span className="font-mono text-xs">{selectedUser.registration_ip || '—'}</span>
              </DetailRow>
              {selectedUser.trial_ends_at && (
                <DetailRow label="Trial ends" icon={Clock}>
                  {formatDate(selectedUser.trial_ends_at)}
                </DetailRow>
              )}
              <DetailRow label="Subscription status" icon={Shield}>
                <Badge variant="outline" className="capitalize">{selectedUser.subscription_status || '—'}</Badge>
              </DetailRow>
            </div>

            {/* ── Connected platforms ─────────────────────────────────── */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5" />
                Connected platforms
              </p>
              {Array.isArray(selectedUser.platforms) && selectedUser.platforms.length > 0
                ? (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedUser.platforms.map(p => (
                      <Badge key={p} variant="secondary">{PLATFORM_LABEL[p] || p}</Badge>
                    ))}
                  </div>
                )
                : <p className="text-sm text-muted-foreground">No platforms connected.</p>}
            </div>

            {/* ── Feature usage ────────────────────────────────────────── */}
            {selectedUser.features && Object.keys(selectedUser.features).length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5" />
                  Feature usage
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(selectedUser.features).map(([k, v]) => (
                    <div key={k} className="p-3 rounded-lg bg-muted/30 border border-border">
                      <p className="text-xs text-muted-foreground">{FEATURE_LABEL[k] || k}</p>
                      <p className="text-lg font-semibold text-foreground">{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ModalFooter>
              <Button variant="outline" onClick={() => setShowUserModal(false)}>
                Close
              </Button>
              {isSuperAdmin && (
                <Button
                  onClick={() => handleUpdateUser(selectedUser.id, {
                    role: selectedUser.role,
                    status: selectedUser.status,
                    subscription_plan: selectedUser.subscription_plan,
                    tier: selectedUser.tier,
                  })}
                  loading={saving}
                >
                  Save Changes
                </Button>
              )}
            </ModalFooter>
          </div>
        )}
      </Modal>

      {/* Ticket Detail Modal */}
      <Modal
        isOpen={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        title={selectedTicket?.subject || 'Ticket'}
        size="lg"
      >
        {selectedTicket && (
          <div className="space-y-4">
            {/* Ticket Info */}
            <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-border">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <Select
                  value={selectedTicket.status}
                  onChange={(e) => handleUpdateTicketStatus(selectedTicket.id, e.target.value)}
                  className="w-32"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </Select>
              </div>
              <Badge variant={selectedTicket.priority === 'urgent' ? 'destructive' : 'outline'}>
                {selectedTicket.priority}
              </Badge>
              <span className="text-sm text-muted-foreground">
                From: {selectedTicket.user?.username || 'Unknown'}
              </span>
              <span className="text-sm text-muted-foreground">
                {formatRelativeTime(selectedTicket.created_at)}
              </span>
            </div>

            {/* Messages Thread */}
            <div className="max-h-80 overflow-y-auto space-y-4 p-1">
              {ticketMessages.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No messages</p>
              ) : (
                ticketMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg ${
                      msg.is_admin_reply 
                        ? 'bg-primary/10 border-l-4 border-primary ml-4' 
                        : 'bg-muted mr-4'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-sm">
                        {msg.is_admin_reply ? '🛡️ Admin' : '👤 User'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatRelativeTime(msg.created_at)}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))
              )}
            </div>

            {/* Reply Input */}
            {selectedTicket.status !== 'closed' && (
              <div className="pt-4 border-t border-border">
                <Textarea
                  placeholder="Type your reply..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={3}
                />
                <div className="flex justify-between items-center mt-3">
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateTicketStatus(selectedTicket.id, 'resolved')}
                  >
                    Mark Resolved
                  </Button>
                  <Button 
                    onClick={handleSendReply} 
                    loading={sendingReply} 
                    disabled={!replyMessage.trim()}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Reply
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Admin;

// ──────────────────────────────────────────────────────────────────────────────
// Demographics row — four cards summarising the user base.
// All styling uses theme tokens (bg-card, text-foreground, etc.) so dark mode
// is automatic. Grid stacks 1→2→4 columns at mobile/tablet/desktop.
// ──────────────────────────────────────────────────────────────────────────────
function DemographicsRow({ aggregates, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-32 bg-card rounded-2xl border border-border animate-pulse" />
        ))}
      </div>
    );
  }
  if (!aggregates) return null;
  const total = aggregates.total || 1;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <BreakdownCard
        title="Tier"
        icon={DollarSign}
        rows={aggregates.tier}
        total={total}
        emptyHint="No users yet"
      />
      <BreakdownCard
        title="Status"
        icon={UserCheck}
        rows={aggregates.status}
        total={total}
        emptyHint="No status data"
      />
      <BreakdownCard
        title="Platforms connected"
        icon={Layers}
        rows={aggregates.platforms_connected}
        total={total}
        labelMap={PLATFORM_LABEL}
        emptyHint="No platform connections yet"
      />
      <BreakdownCard
        title="Features used"
        icon={Activity}
        rows={aggregates.features_used}
        total={total}
        labelMap={FEATURE_LABEL}
        emptyHint="No feature usage yet"
      />
    </div>
  );
}

function BreakdownCard({ title, icon: Icon, rows = {}, total, labelMap, emptyHint }) {
  const entries = Object.entries(rows || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</p>
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        </div>
        {entries.length === 0
          ? <p className="text-sm text-muted-foreground">{emptyHint}</p>
          : (
            <div className="space-y-2">
              {entries.map(([k, v]) => {
                const pct = total > 0 ? Math.round((v / total) * 100) : 0;
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-foreground capitalize truncate">{labelMap?.[k] || k}</span>
                      <span className="text-muted-foreground tabular-nums flex-shrink-0">
                        {v} <span className="opacity-60">· {pct}%</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </CardContent>
    </Card>
  );
}

// Skeleton state for the user table during loadData.
function TableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-12 bg-muted/40 rounded animate-pulse" />
      ))}
    </div>
  );
}

// Tiny label/value row used inside the user-detail modal.
function DetailRow({ label, icon: Icon, children }) {
  return (
    <div className="p-3 rounded-lg bg-muted/20 border border-border">
      <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </p>
      <div className="text-sm text-foreground break-words">{children}</div>
    </div>
  );
}
